import React, { Fragment } from 'react';

import type { ExtensionHandler, ExtensionHandlers } from '@atlaskit/editor-common/extensions';
import { getTemplateExtensionHandler } from './template-variable-extension';

import { EXTENSION_TYPE } from '@confluence/fabric-extension-lib/entry-points/extensionConstants';
import { createMacroExtensionHandler } from '@confluence/fabric-extension-lib/entry-points/createMacroExtensionHandler';
import { getTemplateMigrationExtHandler } from '@confluence/fabric-extension-handlers/entry-points/TemplateMigration';
import { DeadMacroRenderer } from '@confluence/fabric-extension-handlers/entry-points/DeadMacroRenderer';
import { LegacyMacroRenderer } from '@confluence/fabric-extension-legacy-macro-renderer/entry-points/LegacyMacroRendererQuery';
import {
	getMacroAttributesFromADFNode,
	EDITOR,
	getExperienceName,
} from '@confluence/macro-tracker/entry-points/MacroExperienceTracker';
import { MacroExperienceStart } from '@confluence/macro-tracker/entry-points/MacroExperienceStart';
import type { ExperienceAttributes } from '@confluence/experience-tracker/entry-points/ExperienceEvent';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout';
import type { Mode } from '@confluence/macro-tracker/entry-points/types';
// oxlint-disable-next-line import/no-duplicates
import type { Extension } from '@confluence/macro-tracker/entry-points/types';

type ExtensionHandlerProps = {
	node: Extension;
	contentId: string;
	context: {
		insertCss: (styles) => void;
	};
	mode: Mode;
	extensionKey: string;
	attributes?: ExperienceAttributes;
};

const context = {
	insertCss: (styles) => styles._insertCss(),
};

export const getTemplateExtensionHandlers = (): ExtensionHandlers => ({
	[EXTENSION_TYPE.MACRO_CORE]: getTemplateMacroExtensionHandler(),
	[EXTENSION_TYPE.MIGRATION]: getTemplateMigrationExtHandler(),
	[EXTENSION_TYPE.TEMPLATE]: getTemplateExtensionHandler,
});

export const getTemplateMacroExtensionHandler = (): ExtensionHandler<any> =>
	createMacroExtensionHandler((extension) => {
		const { extensionKey } = extension;
		const attributes = getMacroAttributesFromADFNode(extension);
		const templateId = '0';

		const macroDefaultProps: ExtensionHandlerProps = {
			node: extension,
			contentId: templateId,
			mode: 'editor' as Mode,
			context,
			extensionKey,
			attributes,
		};
		let implementation;

		switch (extensionKey) {
			// whitelist (CFE-2326)
			case 'detailssummary':
			case 'recently-updated':
			case 'children':
			case 'attachments':
			case 'contentbylabel':
			case 'profile-picture':
			case 'pagetree':
			case 'include':
			case 'tasks-report-macro':
			case 'profile':
			case 'widget':
			case 'excerpt-include':
			case 'spaces':
				implementation = <LegacyMacroRenderer {...macroDefaultProps} />;
				break;
			default:
				implementation = <DeadMacroRenderer {...macroDefaultProps} />;
		}

		return (
			<Fragment>
				<MacroExperienceStart
					name={getExperienceName(EDITOR, extension)}
					contentId={templateId}
					mode={EDITOR}
					extensionKey={extensionKey}
					attributes={attributes}
					timeout={ExperienceTimeout.MACRO_LOAD}
				/>
				{implementation}
			</Fragment>
		);
	});
